import { useMutation, useQuery } from "@tanstack/react-query";
import { Buton, Ikon, Iskelet, Kart, Tablo } from "@koclukpanel/ui";
import {
  ApiHatasi,
  raporEkBolumleri,
  raporPdfBaglantisi,
  type RaporDenemesi,
  type RaporEkBolumleri as EkBolumler,
  type RaporGorusmesi,
} from "./api";

/**
 * VELİ RAPORU — GÖRÜŞMELER VE DENEME SONUÇLARI (sürüm 9a; UNİKOÇ "Veli Raporu Detayları")
 *
 * Bu bölüm VELİYE GİDECEK hâliyle çiziliyor: görüşme özeti yalnız veliye
 * açıksa geliyor, değilse kartta yalnız tarih/tür/süre var. Sunucu bunu
 * zaten süzüyor (`rapor-ek-bolumler.ts`); ekran ikinci bir kural kurmuyor,
 * yalnız "özet veliye kapalı" diye söylüyor ki koç neden boş olduğunu bilsin.
 * Korumalı PDR notu bu uca hiç girmiyor.
 */

const GORUSME_TURU: Record<RaporGorusmesi["tur"], string> = {
  COACHING: "Koçluk görüşmesi",
  MAKEUP: "Telafi görüşmesi",
  PARENT_MEETING: "Veli görüşmesi",
};

const tarihMetni = (t: string): string =>
  new Date(t).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" });

const netMetni = (n: number): string =>
  (Math.round(n * 10) / 10).toLocaleString("tr-TR", { maximumFractionDigits: 1 });

export function RaporEkBolumleri({ raporId }: { raporId: string }) {
  const ek = useQuery<EkBolumler>({
    queryKey: ["rapor-ek-bolumler", raporId],
    queryFn: () => raporEkBolumleri(raporId),
  });

  if (ek.isPending) return <Iskelet satir={3} />;
  if (ek.isError) {
    return (
      <Kart gorunum="unk" ikon="gorusme" baslik="Görüşmeler ve deneme sonuçları">
        <p role="alert" className="text-hata-700 text-sm">
          Görüşme ve deneme bölümleri alınamadı.
          {ek.error instanceof ApiHatasi ? ` ${ek.error.message}` : ""}
        </p>
      </Kart>
    );
  }

  const { gorusmeler, denemeler, gorusmeSayisi } = ek.data;
  return (
    <>
      <Kart
        gorunum="unk"
        ikon="gorusme"
        baslik="Görüşmeler"
        aciklama={`${gorusmeSayisi} kayıt · veliye açık özetler raporda görünür`}
      >
        {gorusmeler.length === 0 ? (
          <p className="text-notr-600 mt-2 text-sm">Bu dönemde gerçekleşen görüşme kaydı yok.</p>
        ) : (
          <ul className="mt-2 grid gap-3 md:grid-cols-2">
            {gorusmeler.map((g) => (
              <li
                key={g.id}
                className="border-mor-kenar bg-notr-0 rounded-2xl border p-4 text-sm shadow-(--golge-unk-kart)"
              >
                {/* UNİKOÇ görüşme kartı: tarih hapı solda, süre sağda; altında büyük harf başlık. */}
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="border-mor-kenar text-metin-koyu inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold">
                    <Ikon ad="takvim" className="size-3.5" />
                    {tarihMetni(g.tarih)}
                  </span>
                  {g.sureDk !== null && (
                    <span className="border-mor-kenar text-metin-koyu rounded-full border px-3 py-1 text-xs font-bold">
                      {g.sureDk} dk
                    </span>
                  )}
                </div>
                <p className="text-metin-koyu mt-3 font-bold uppercase">{GORUSME_TURU[g.tur]}</p>
                {g.ozet ? (
                  <p className="text-notr-800 mt-2 whitespace-pre-line">{g.ozet}</p>
                ) : (
                  <p className="text-notr-600 mt-2 text-xs">
                    Özet veliye kapalı ya da yazılmamış — raporda yalnız tarih görünür.
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </Kart>

      <Kart
        gorunum="unk"
        ikon="deneme"
        baslik="Deneme sonuçları"
        aciklama={`${denemeler.length} kayıt`}
      >
        {denemeler.length === 0 ? (
          <p className="text-notr-600 mt-2 text-sm">Bu dönemde girilmiş deneme sonucu yok.</p>
        ) : (
          <div className="mt-2">
            <Tablo<RaporDenemesi>
              baslikTuru="degrade"
              satirlar={denemeler}
              anahtar={(d) => `${d.ad}-${d.tarih}`}
              sutunlar={[
                {
                  baslik: "Deneme",
                  mobil: true,
                  hucre: (d) => <span className="font-semibold">{d.ad}</span>,
                },
                { baslik: "Tarih", hucre: (d) => tarihMetni(d.tarih) },
                { baslik: "D", hucre: (d) => d.dogru },
                { baslik: "Y", hucre: (d) => d.yanlis },
                { baslik: "B", hucre: (d) => d.bos },
                {
                  baslik: "Net",
                  mobil: true,
                  hucre: (d) => <strong className="tabular-nums">{netMetni(d.net)}</strong>,
                },
              ]}
            />
          </div>
        )}
      </Kart>
    </>
  );
}

/**
 * PDF indirme — bağlantı TIKLANINCA isteniyor (15 dk geçerli; listeyle üretilseydi
 * açık bırakılan sayfada ölmüş olurdu — `OgrenciDosyasi.tsx` ile aynı gerekçe).
 */
export function usePdfIndir() {
  return useMutation({
    mutationFn: (raporId: string) => raporPdfBaglantisi(raporId),
    onSuccess: (s) => {
      window.open(s.url, "_blank", "noopener,noreferrer");
    },
  });
}

/** PDF düğmesi + hata satırı — rapor detayında. Yalnız veliye gönderilmiş raporda çizilir. */
export function PdfIndirDugmesi({ raporId }: { raporId: string }) {
  const indir = usePdfIndir();
  return (
    <div className="flex flex-col items-end gap-1">
      <Buton yukleniyor={indir.isPending} onClick={() => indir.mutate(raporId)}>
        PDF İndir
      </Buton>
      {indir.error instanceof ApiHatasi && (
        <p role="alert" className="text-hata-700 text-xs">
          {indir.error.message}
        </p>
      )}
    </div>
  );
}
